import type { Artist, SessionLength } from '@/domain/types';

// Final session price the studio bills for a booking with this artist.
export function priceForArtist(artist: Artist, session: SessionLength): number {
  switch (session) {
    case 'small':
      return artist.rates.smallAud;
    case 'half-day':
      return artist.rates.halfDayAud;
    case 'full-day':
      return artist.rates.fullDayAud;
    case 'multi-session':
      // Multi-session: treat as two full days for billing purposes.
      return artist.rates.fullDayAud * 2;
  }
}

// Pricing from duration: rounds up to the cheapest session bracket that fits.
export function priceForDuration(artist: Artist, durationMin: number): number {
  if (durationMin <= 120) return artist.rates.smallAud;
  if (durationMin <= 300) return artist.rates.halfDayAud;
  return artist.rates.fullDayAud;
}

// Standard deposit taken per session length, network-wide. The artist rate card
// varies the *day rate* per artist; the deposit schedule is a studio standard.
// SINGLE SOURCE OF TRUTH for deposit amounts - every UI (self-book generator,
// deposit-link modal, universal self-book page) reads amounts from here.
export const SESSION_DEPOSIT_AUD: Record<SessionLength, number> = {
  small: 200,
  'half-day': 400,
  'full-day': 800,
  'multi-session': 500,
};

// Human-facing copy for a session length. Amounts always come from
// SESSION_DEPOSIT_AUD - never re-hardcoded alongside the labels.
export const SESSION_META: Record<SessionLength, { label: string; description: string }> = {
  small: { label: 'Small', description: '90 min · single sitting' },
  'half-day': { label: 'Half day', description: '4 hours · half-sleeve' },
  'full-day': { label: 'Full day', description: '8 hours · sleeve / large piece' },
  'multi-session': { label: 'Multi-session', description: 'Booked across several sittings' },
};

// Session bracket from raw duration - same brackets as priceForDuration.
export function sessionLengthForDuration(durationMin: number): SessionLength {
  if (durationMin <= 120) return 'small';
  if (durationMin <= 300) return 'half-day';
  return 'full-day';
}

// Studio-standard deposit for a custom-built session, by duration bracket.
export function depositForDuration(durationMin: number): number {
  return SESSION_DEPOSIT_AUD[sessionLengthForDuration(durationMin)];
}

export const SESSION_DURATION_MIN: Record<SessionLength, number> = {
  small: 90,
  'half-day': 240,
  'full-day': 480,
  'multi-session': 240,
};

export function depositForSession(session: SessionLength): number {
  return SESSION_DEPOSIT_AUD[session];
}

/**
 * What an EXISTING booking is billed at — the single price every surface should
 * read for a booking that already exists.
 *
 * `dayRateAud` is the price the booking was actually struck at (a manager can
 * override the rate card at booking time), so it wins; the artist's rate card is
 * only the fallback for a booking that never recorded one. Payouts already read
 * `dayRateAud` (`payoutFlow.computeDayPayouts`), so any screen that re-derives
 * from `priceForArtist` instead can show a different number for the same
 * session — the Floor listing one price while the artist's envelope is computed
 * from another. Use this everywhere a booking's price is displayed or summed.
 *
 * `priceForArtist` remains correct for pricing a booking that does not exist yet
 * (the new/edit booking modals, the rate-card tiers).
 */
export function bookingPriceAud(
  booking: { sessionLength: SessionLength; dayRateAud?: number },
  artist: Artist | undefined,
): number {
  if (booking.dayRateAud != null) return booking.dayRateAud;
  return artist ? priceForArtist(artist, booking.sessionLength) : 0;
}

export interface RateCardTier {
  session: SessionLength;
  label: string;
  dayRateAud: number;
  depositAud: number;
}

// The three primary session tiers shown as cards in the booking modals, priced
// from the artist's rate card (day rate) plus the standard deposit schedule.
const RATE_CARD_TIERS: Array<{ session: SessionLength; label: string }> = [
  { session: 'small', label: 'Small' },
  { session: 'half-day', label: 'Half day' },
  { session: 'full-day', label: 'Full day' },
];

export function rateCardTiers(artist: Artist | undefined): RateCardTier[] {
  return RATE_CARD_TIERS.map((t) => ({
    session: t.session,
    label: t.label,
    dayRateAud: artist ? priceForArtist(artist, t.session) : 0,
    depositAud: SESSION_DEPOSIT_AUD[t.session],
  }));
}
